import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { SERVICES, type Service } from "@oathera/contract";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The `GET /api/v1/services/{service}` read for the Service page (task 11.5; design 11, 12.2).
 *
 * The response shape is the contract's `ServiceDetail` schema — taken straight from the generated
 * `@oathera/contract/api`, never redefined here. `ServiceDetail` extends `ServiceHealth` (status,
 * instance counts, versions, profiles, core state, last heartbeat, stale) with the detail the
 * screen needs: `golden_signals`, per-route `routes`, `instances` (with `restarts_in_range`), and
 * the admin-deep-link `admin_link`. The operation is role `viewer` and takes an optional `from`/`to`
 * window (Unix ms); the screen passes the window resolved from the shared time-range so pausing
 * freezes the snapshot exactly as the Overview screen does (design 12.2, FR-13).
 *
 * The fetch goes through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError}
 * and the shell shows "Waking the control plane"; a 404 for a service the core does not know
 * surfaces as `not_found`. The query is retried per `query-client.ts`.
 */

/** The contract `ServiceDetail` response body. */
export type ServiceDetail = components["schemas"]["ServiceDetail"];

/** The resolved time window the query ran for, so the screen can label the snapshot honestly. */
export interface ServiceWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseServiceResult {
  readonly query: UseQueryResult<ServiceDetail, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: ServiceWindow;
}

/**
 * Narrow an untrusted `:service` route param to a contract {@link Service} enum member. The route
 * param is arbitrary text from the address bar; a value that is not one of the catalog services is
 * NOT a service and the screen must render an honest invalid state rather than issue a request for
 * a service that cannot exist. `SERVICES` is the generated runtime tuple, the single source of
 * truth for the enum.
 */
export function isServiceParam(value: string | undefined): value is Service {
  return value !== undefined && (SERVICES as readonly string[]).includes(value);
}

/**
 * Fetch the detail for one service in the current time window. The window is resolved from
 * `useTimeRange`:
 * - Live: a window ending "now" for the selected preset, bucketed to a 10s grid so the query key is
 *   stable between ticks (matching `use-overview.ts`); TanStack's `staleTime` smooths the rest.
 * - Paused / custom: the frozen window, so the snapshot stops advancing (design 12.2, FR-13 AC2).
 *
 * `service` must already be a validated {@link Service}; an invalid route param is handled by the
 * screen (via {@link isServiceParam}) and never reaches this hook. The query is still created when
 * `enabled` is false (the hook obeys React's rules of hooks); callers pass `enabled: false` when
 * there is nothing to fetch so no request goes out.
 */
export function useService(service: Service, options: { enabled?: boolean } = {}): UseServiceResult {
  const timeRange = useTimeRange();
  const enabled = options.enabled ?? true;

  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: ServiceWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const query = useQuery<ServiceDetail, ApiError>({
    queryKey: ["service", service, window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<ServiceDetail>(`/services/${service}`, {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    enabled,
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
